import { useCallback, useState } from "react";
import {
  Body,
  Box,
  Button,
  Heading,
  InlineMessage,
  LocalizedString,
  OTP,
  PinInput,
  PositiveInteger,
  Space,
  Stack,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import { RuntimeLocaleKey, useFormatMessage } from "../intl";
import { TaskEither } from "fp-ts/TaskEither";
import { SmartKeyVariant } from "./SmartKeyState";

import { Option } from "fp-ts/Option";
import { constFalse, constTrue, constVoid, pipe } from "fp-ts/function";
import { option, taskEither } from "fp-ts";

type PINValidationStatus = "ready" | "invalid" | "noAttemptsLeft";

type Props<L extends PositiveInteger> = {
  pinLength: L;
  onVerifyOTP: (otp: OTP<L>) => TaskEither<unknown, void>;
  onSendPush: () => unknown;
  canSendPush: boolean;
  variant: SmartKeyVariant;
  status: PINValidationStatus;
  transactionId: Option<LocalizedString>;
};

function SendPushLink(props: { onClick: () => unknown }) {
  const formatMessage = useFormatMessage();
  const isMobileLayout = useIsMobileLayout();
  return (
    <Box
      style={{ gap: "5px" }}
      column={isMobileLayout}
      hAlignContent={isMobileLayout ? "center" : "left"}
    >
      {formatMessage("SmartKey.sendPushText")}
      <Button
        variant="text"
        size="default"
        label={formatMessage("SmartKey.sendPushLink")}
        ariaLabel={formatMessage("SmartKey.sendPushLink.linkPurpose")}
        action={props.onClick}
      />
    </Box>
  );
}

export function QRCodePINBottom<L extends PositiveInteger>(props: Props<L>) {
  const formatMessage = useFormatMessage();
  const [otp, setOTP] = useState("");
  const [validationError, setValidationError] = useState<
    Option<LocalizedString>
  >(option.none);
  const submitLabel = ((): LocalizedString => {
    switch (props.variant) {
      case "authorization":
        return formatMessage("SmartKey.verifyOTPSubmitLabel.authorization");
      case "authentication":
        return formatMessage("SmartKey.verifyOTPSubmitLabel.authentication");
    }
  })();
  const onSubmit = useCallback(
    (otp: string) =>
      pipe(
        otp,
        OTP(props.pinLength).decode,
        taskEither.fromEither,
        taskEither.mapLeft(constVoid),
        taskEither.chainFirst(() =>
          taskEither.rightIO(() => setValidationError(option.none))
        ),
        taskEither.alt(() =>
          taskEither.leftIO(() =>
            setValidationError(
              option.some(
                formatMessage("Identification.otp.codeTooShort", {
                  length: props.pinLength,
                })
              )
            )
          )
        ),
        taskEither.chain(props.onVerifyOTP)
      ),
    []
  );
  const inlineErrorMessage = pipe(
    validationError,
    option.alt(() =>
      pipe(
        props.status,
        option.fromPredicate(s => s === "invalid"),
        option.map(() => formatMessage("SmartKey.invalidOTPInserted"))
      )
    )
  );
  const displayPINInput = ((): boolean => {
    switch (props.status) {
      case "ready":
      case "invalid":
        return true;
      case "noAttemptsLeft":
        return false;
    }
  })();

  const isMobileLayout = useIsMobileLayout();

  return (
    <Box width="100%" vAlignContent="top" grow>
      <Stack column units={5} grow style={{ width: "100%" }}>
        <Box
          grow
          style={{
            justifyContent: "space-between",
            flexFlow: isMobileLayout ? "column-reverse" : "row",
          }}
          column={isMobileLayout}
        >
          <Heading size="small" weight="bold">
            {formatMessage("SmartKey.qrCodePINTitle")}
          </Heading>
          {pipe(
            props.transactionId,
            option.map(transactionId => (
              <Body size="medium" weight="regular">
                {formatMessage("SmartKey.transactionId", {
                  transactionId,
                })}
              </Body>
            )),
            option.toNullable
          )}
        </Box>
        {displayPINInput && (
          <PinInput
            value={otp}
            onChange={setOTP}
            length={props.pinLength}
            onSubmit={onSubmit}
            submitLabel={submitLabel}
            autoFocus
            hasError={pipe(
              inlineErrorMessage,
              option.fold(constFalse, constTrue)
            )}
            submit="manual"
            issues={option.none}
            ariaLabelMessage={formatMessage(
              "generic.otp.input.alt" as RuntimeLocaleKey
            )}
            inlineSubmitButtonAlignment={!isMobileLayout}
          />
        )}
        {pipe(
          inlineErrorMessage,
          option.map(error => (
            <>
              <Space units={1} />
              <InlineMessage size="small" type="error" message={error} />
            </>
          )),
          option.toNullable
        )}
        {props.canSendPush && <SendPushLink onClick={props.onSendPush} />}
      </Stack>
    </Box>
  );
}
